import {
  getUniqueInstallId,
  isDevEnvironment,
  logBrazeError,
  logEvent,
} from '../../Utils'

// Cache for the Braze SDK
let brazeSDKCache: any = null

/**
 * Gets the Braze SDK, caching it after the first import
 * @returns The Braze SDK or null if import fails
 */
export const getBrazeSDK = async () => {
  if (brazeSDKCache) {
    return brazeSDKCache
  }

  try {
    brazeSDKCache = await import('@braze/web-sdk')
    return brazeSDKCache
  } catch (error) {
    console.error('Error importing Braze SDK:', error)
    logEvent('brazeError_importSDK_web_main', {
      provider: 'braze',
      error,
      product: 'web_main',
    })
    return null
  }
}

type BrazeInitConfig = {
  baseUrl: string
  enableLogging: boolean
  minimumIntervalBetweenTriggerActionsInSeconds: number
  allowUserSuppliedJavascript: boolean
}

/**
 * Creates the Braze initialization configuration
 * @returns Braze configuration object
 */
const createBrazeConfig = (): BrazeInitConfig => ({
  baseUrl: process.env.REACT_APP_BRAZE_API_URL || 'sdk.iad-05.braze.com',
  enableLogging: isDevEnvironment(),
  minimumIntervalBetweenTriggerActionsInSeconds: 10,
  allowUserSuppliedJavascript: true,
})

/**
 * Adds Firebase pseudo ID alias to Braze user
 * @param braze Braze SDK instance
 * @returns Whether alias was added successfully
 */
const addFirebasePseudoIdAlias = async (braze: any): Promise<boolean> => {
  try {
    const installId = await getUniqueInstallId()

    if (!installId) {
      return false
    }

    if (isDevEnvironment()) {
      console.log(
        'Adding firebase_pseudo_id alias to Braze user. Install ID:',
        installId
      )
    }

    const user = braze?.getUser?.()

    if (!user || typeof user.addAlias !== 'function') {
      logBrazeError('brazeError_alias_missing_user_or_method')
      return false
    }

    try {
      const aliasResult = user.addAlias(installId, 'firebase_pseudo_id')

      if (isDevEnvironment()) {
        console.log('Braze addAlias result:', aliasResult)
      }

      // Flush if possible but don't fail the whole operation if errors
      try {
        braze?.requestImmediateDataFlush?.()
        if (isDevEnvironment()) {
          console.log('Braze data flushed after alias addition')
        }
      } catch (flushError) {
        console.warn('Error flushing Braze data:', flushError)
      }

      return Boolean(aliasResult)
    } catch (aliasError) {
      console.error('Error calling addAlias:', aliasError)
      logBrazeError('brazeError_addAlias_call_failed', { error: aliasError, installId })
      return false
    }
  } catch (error) {
    console.error('Error adding alias to Braze user:', error)
    logBrazeError('brazeError_alias_exception', { error })
    return false
  }
}

/**
 * Initializes the Braze SDK with configuration
 * @param braze Braze SDK instance
 * @returns Whether SDK initialization was successful
 */
const initializeBrazeSDK = (braze: any): boolean => {
  const apiKey = process.env.REACT_APP_BRAZE_API_KEY
  if (!apiKey) {
    throw new Error('Braze API key is required')
  }

  const config = createBrazeConfig()
  const { initialize } = braze

  return initialize(apiKey, config)
}

/**
 * Sets up Braze user session
 * @param braze Braze SDK instance
 * @param userId Optional user ID for identified users
 */
const setupBrazeSession = async (braze: any, userId?: string): Promise<void> => {
  if (userId) {
    const { changeUser } = braze
    changeUser(userId)
  }

  const { openSession } = braze
  openSession()

  await new Promise((resolve) => setTimeout(resolve, 100))

  void addFirebasePseudoIdAlias(braze)
}

/**
 * Core function to initialize Braze with common configuration
 * @param userId Optional user ID for identified users
 * @returns Whether initialization was successful
 */
export const initBrazeCore = async (userId?: string): Promise<boolean> => {
  if (!process.env.REACT_APP_BRAZE_API_KEY) {
    return false
  }

  try {
    const braze = await getBrazeSDK()
    if (!braze) {
      console.error('Failed to load Braze SDK')
      return false
    }

    const initSuccess = initializeBrazeSDK(braze)
    if (!initSuccess) {
      console.error('Failed to initialize Braze SDK')
      return false
    }

    await setupBrazeSession(braze, userId)
    return true
  } catch (error) {
    const context = userId ? 'initBraze' : 'initBrazeAnonymous'
    console.error(`Error initializing Braze in ${context}:`, error)
    logEvent(`brazeError_${context}_web_main`, {
      provider: 'braze',
      error,
      product: 'web_main',
    })
    return false
  }
}

/**
 * Signs out the current user from Braze
 * @returns Whether the sign out was successful
 */
export const signOutBraze = async (): Promise<boolean> => {
  try {
    const braze = await getBrazeSDK()
    if (!braze) return false

    const { wipeData } = braze
    wipeData()

    return true
  } catch (error) {
    logEvent('brazeError_signOut_web_main', {
      provider: 'braze',
      error,
      product: 'web_main',
    })
    return false
  }
}
